import React from 'react';
import { Box, Select, MenuItem, Typography, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  buildControlMenuProps,
  getControlTokens,
} from '../common/controls';
import AccessTierBadge from '../common/AccessTierBadge';

/**
 * SimpleSelect - A standardized dropdown component matching DashboardFields visual style
 *
 * @param {object} props
 * @param {Array} props.options - Array of options (strings, or objects with value/label or id/field)
 * @param {*} props.value - Current selected value
 * @param {function} props.onChange - Callback when value changes: (value) => void
 * @param {string} props.placeholder - Optional placeholder text (default: 'Select...')
 * @param {number|string} props.width - Optional width (default: 'auto')
 * @param {function} props.getOptionValue - Optional function to extract value from option
 * @param {function} props.getOptionLabel - Optional function to extract label from option
 * @param {object} props.sx - Optional style overrides for the container
 * @param {Set} props.proOptions - Optional Set of gated option values that require PRO
 * @param {boolean} props.isPaid - Whether user has paid subscription
 * @param {function} props.onRequirePro - Optional callback when a gated option is clicked
 * @param {Set} props.lockedOptions - Optional Set of values gated by an arbitrary entitlement
 * @param {boolean} props.hasLockedAccess - Whether the arbitrary entitlement is available
 * @param {function} props.onLockedOptionSelect - Called when a locked option is invoked
 * @param {string} props.ariaLabel - Accessible name for the select control
 */
const SimpleSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  width = 'auto',
  height,
  getOptionValue,
  getOptionLabel,
  sx = {},
  proOptions,
  isPaid = false,
  onRequirePro,
  lockedOptions,
  hasLockedAccess = isPaid,
  onLockedOptionSelect,
  lockedTooltip = 'Research Suite required',
  menuMinWidth,
  wrapLabels = false,
  ariaLabel,
  controlContext = 'filterBar',
  menuContext = controlContext,
}) => {
  const theme = useTheme();
  const tokens = getControlTokens(theme, controlContext);
  const controlHeight = height || tokens.height;
  const usesGenericLock = lockedOptions !== undefined;
  const gatedOptions = usesGenericLock ? lockedOptions : proOptions;
  const hasGatedAccess = usesGenericLock ? hasLockedAccess : isPaid;
  const handleLockedOption = onLockedOptionSelect || onRequirePro;

  // Auto-detect option format and create extractors
  const extractValue = (opt) => {
    if (getOptionValue) return getOptionValue(opt);
    if (typeof opt === 'string') return opt;
    if (opt.value !== undefined) return opt.value;
    if (opt.id !== undefined) return opt.id;
    if (opt.field !== undefined) return opt.field;
    return opt;
  };

  const extractLabel = (opt) => {
    if (getOptionLabel) return getOptionLabel(opt);
    if (typeof opt === 'string') return opt;
    if (opt.label !== undefined) return opt.label;
    if (opt.field !== undefined) return opt.field;
    return String(opt);
  };

  const extractIcon = (opt) => {
    if (typeof opt === 'object' && opt.icon) return opt.icon;
    return null;
  };

  // Find the option that matches the current value for display
  const getDisplayOption = () => {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return options.find((opt) => extractValue(opt) === value) || null;
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    if (!hasGatedAccess && gatedOptions?.has(newValue) && handleLockedOption) {
      handleLockedOption(newValue);
      return;
    }
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <Box
      sx={{
        height: controlHeight,
        width: width,
        display: 'flex',
        flexDirection: 'row',
        ...sx,
      }}
    >
      <Select
        size="small"
        value={value ?? ''}
        onChange={handleChange}
        inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
        displayEmpty
        sx={{
          width: '100%',
          height: controlHeight,
          backgroundColor: tokens.surface,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '& .MuiSelect-select': {
            height: '100%',
            minHeight: '0 !important',
            display: 'flex',
            alignItems: 'center',
            py: 0,
            boxSizing: 'border-box',
          },
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
        }}
        renderValue={() => {
          const matchedOption = getDisplayOption();
          const displayLabel = matchedOption ? extractLabel(matchedOption) : placeholder;
          const displayIcon = matchedOption ? extractIcon(matchedOption) : null;
          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: tokens.fontSize,
                display: 'flex',
                alignItems: 'center',
                gap: displayIcon ? '6px' : 0,
                lineHeight: 1,
                color: theme.palette.text.secondary,
              }}
            >
              {displayIcon && (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: theme.palette.text.secondary, minWidth: '18px' }}>
                  {displayIcon}
                </Box>
              )}
              {displayLabel}
            </Typography>
          );
        }}
        MenuProps={buildControlMenuProps(theme, menuContext, {
          PaperProps: { sx: { minWidth: menuMinWidth } },
        })}
      >
        {options.map((opt, index) => {
          const optValue = extractValue(opt);
          const optLabel = extractLabel(opt);
          const optIcon = extractIcon(opt);
          const isSelected = optValue === value;
          const hasIcons = options.some((o) => extractIcon(o));
          const isProGated = !hasGatedAccess && gatedOptions?.has(optValue);
          const isTierGated = usesGenericLock && gatedOptions?.has(optValue);

          return (
            <MenuItem
              key={optValue ?? index}
              value={optValue}
              sx={{
                minHeight: wrapLabels ? '38px' : hasIcons ? '32px' : '26px',
                height: wrapLabels ? 'auto' : hasIcons ? '32px' : '26px',
                py: 0.25,
                px: 1.5,
                opacity: isProGated ? 0.5 : 1,
                backgroundColor: isSelected
                  ? theme.palette.action.selected
                  : 'transparent',
                borderLeft: isSelected
                  ? `2px solid ${theme.palette.primary.main}`
                  : '2px solid transparent',
                transition: 'background-color 0.1s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '12px',
                  lineHeight: 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: optIcon ? '6px' : 0,
                  whiteSpace: wrapLabels ? 'normal' : 'nowrap',
                  overflowWrap: wrapLabels ? 'anywhere' : 'normal',
                  fontWeight: 600,
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
                variant="body2"
                title={optLabel}
              >
                {optIcon && (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: theme.palette.text.secondary, mr: '6px', minWidth: '18px' }}>
                    {optIcon}
                  </Box>
                )}
                {optLabel}
              </Typography>
              {isTierGated && (
                <AccessTierBadge
                  requiredTier="research"
                  locked={isProGated}
                  compact
                  sx={{ ml: 1 }}
                />
              )}
              {!usesGenericLock && isProGated && (
                <Tooltip title="Upgrade to Core" arrow placement="right">
                  <Chip
                    label="PRO"
                    size="small"
                    variant="outlined"
                    sx={{
                      ml: 1,
                      fontSize: '8px',
                      height: 14,
                      fontWeight: 'bold',
                      color: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                </Tooltip>
              )}
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
};

export default SimpleSelect;
